/**
 * IpoPage — /ipo landing page.
 *
 * Shows all IPOs bucketed into Open / Upcoming / Recently Listed /
 * Historical / Pending Prospectus, plus a track-record card at the bottom.
 *
 * Accessible to any authenticated user during beta (matches the current
 * beta policy that unlocks all premium features for free).
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { apiGet, formatDateTime, formatNumber, formatLKR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import EmptyState from "@/components/common/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, Calendar, Sparkles, ArrowRight, Rocket, Clock, Trophy, FileText } from "lucide-react";

const SIGNAL_BADGE_CLASSES = {
  "STRONG SUBSCRIBE": "bg-[hsl(var(--success))] text-white border-transparent",
  "SUBSCRIBE": "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  "WATCHLIST": "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
  "AVOID": "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};

const IpoSignalBadge = ({ signal, conviction }) => {
  if (!signal) return <Badge variant="outline" className="text-[10px]">Pending</Badge>;
  const label = conviction ? `★ ${signal} · ${conviction}` : `★ ${signal}`;
  const cls = SIGNAL_BADGE_CLASSES[signal.toUpperCase()] || "bg-muted text-muted-foreground";
  return (
    <Badge className={`gap-1 ${cls}`} data-testid={`ipo-signal-${signal.replace(/\s+/g, "-").toLowerCase()}`}>
      {label}
    </Badge>
  );
};

const IpoCard = ({ ipo, testId }) => {
  const rec = ipo.ipo_recommendation || {};
  const sc = ipo.scoring || {};
  const det = ipo.ipo_details || {};
  const subCloseDate = det.subscription_closes ? new Date(det.subscription_closes) : null;
  const listingDate = det.listing_date ? new Date(det.listing_date) : null;
  // Hide the 6-layer composite score badge for pre-listing / recent IPOs.
  // The composite is meaningful only for fully-listed stocks with
  // ≥ 12 months of filed consolidated accounts. For a new IPO the
  // missing BVPS/ROE pushes the score to ~20 and stamps AVOID, which
  // directly contradicts the qualitative IPO recommendation
  // (e.g. STRONG SUBSCRIBE at score 20). Match the rule used inside
  // /ipo/:symbol → HeaderCard so the UX is uniform across pages.
  const status = (det.ipo_status || "").toLowerCase();
  const daysSinceListing = listingDate
    ? Math.floor((Date.now() - listingDate.getTime()) / (1000 * 60 * 60 * 24))
    : -1;
  const isRecentIpo = ["upcoming", "open", "closed"].includes(status)
    || ipo.status === "pending_prospectus"
    || daysSinceListing < 0
    || daysSinceListing < 365;
  return (
    <Card className="border bg-card hover:border-primary/40 transition-colors" data-testid={testId || `ipo-card-${ipo.symbol}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="font-mono text-[11px] text-muted-foreground">{ipo.symbol}</div>
            <CardTitle className="font-display text-base truncate" title={ipo.company_name}>
              {ipo.company_name}
            </CardTitle>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {ipo.sector || "Sector —"}
            </div>
          </div>
          <IpoSignalBadge signal={rec.signal} conviction={rec.conviction} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">IPO price</div>
            <div className="font-semibold">{det.ipo_price ? `Rs. ${formatNumber(det.ipo_price, 2)}` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Score</div>
            <div className="font-semibold">
              {isRecentIpo ? (
                <span
                  className="text-[11px] text-muted-foreground italic"
                  title="Composite score isn't meaningful for pre-listing or recently-listed IPOs — see the IPO recommendation above."
                >
                  n/a
                </span>
              ) : (
                <>
                  {sc.composite_score != null ? sc.composite_score : "—"}
                  {sc.signal && (
                    <Badge variant="outline" className="ml-1 text-[10px]">{sc.signal}</Badge>
                  )}
                </>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {subCloseDate ? "Closes" : listingDate ? "Lists" : "Status"}
            </div>
            <div className="font-semibold text-xs">
              {subCloseDate
                ? subCloseDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                : listingDate
                  ? listingDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                  : (ipo.status === "pending_prospectus" ? "Awaiting prospectus" : "—")}
            </div>
          </div>
        </div>

        <Button
          asChild
          size="sm"
          variant="outline"
          className="w-full gap-2"
          data-testid={`ipo-card-view-${ipo.symbol}`}
        >
          <Link to={`/ipo/${ipo.symbol}`}>
            View Full Analysis <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};

const Section = ({ title, Icon, accent, children, testId, emptyMessage = "No IPOs in this bucket." }) => (
  <div data-testid={testId} className="space-y-3">
    <div className="flex items-center gap-2">
      {Icon && <Icon className={`h-4 w-4 ${accent || "text-muted-foreground"}`} />}
      <h2 className="font-display font-semibold text-sm uppercase tracking-wide">
        {title}
      </h2>
    </div>
    {children || (
      <div className="rounded-md border border-dashed px-4 py-6 text-center text-xs text-muted-foreground">
        {emptyMessage}
      </div>
    )}
  </div>
);

const TrackRecordCard = ({ stats }) => {
  if (!stats) return null;
  const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`);
  return (
    <Card className="border bg-card" data-testid="ipo-track-record-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-[hsl(var(--success))]" />
          <CardTitle className="font-display">Our IPO Track Record</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Transparent scorecard of every IPO recommendation we've published.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">IPOs analysed</div>
            <div className="font-semibold text-lg">{stats.total_ipos_analysed || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Accuracy</div>
            <div className="font-semibold text-lg">
              {stats.accuracy_pct != null ? `${stats.accuracy_pct.toFixed(0)}%` : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Avg return on SUBSCRIBE
            </div>
            <div className={`font-semibold text-lg ${(stats.average_return_subscribe || 0) >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
              {fmtPct(stats.average_return_subscribe)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Avoided losses on AVOID
            </div>
            <div className={`font-semibold text-lg ${(stats.average_return_avoid || 0) <= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
              {fmtPct(stats.average_return_avoid)}
            </div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-4 text-[11px] text-muted-foreground">
          <div data-testid="ipo-track-record-subscribe">
            SUBSCRIBE calls: {stats.subscribe_recommendations || 0} · Correct: {stats.subscribe_correct || 0}
          </div>
          <div data-testid="ipo-track-record-avoid">
            AVOID calls: {stats.avoid_recommendations || 0} · Correct: {stats.avoid_correct || 0}
          </div>
        </div>
        {(!stats.total_ipos_analysed || stats.total_ipos_analysed === 0) && (
          <div className="mt-3 rounded-md border border-dashed px-3 py-2 text-[11px] text-muted-foreground" data-testid="ipo-track-record-stub">
            Once we've analysed a few IPOs with post-listing price data, this
            panel will show our accuracy rate and average returns.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const IpoPage = () => {
  const { isAdmin } = useAuth();
  const [buckets, setBuckets] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([apiGet("/ipo/list"), apiGet("/ipo/track-record")])
      .then(([list, tr]) => { setBuckets(list); setStats(tr); })
      .catch((e) => setError(e?.message || "Failed to load IPOs"))
      .finally(() => setLoading(false));
  }, []);

  const totalCount = useMemo(() => {
    if (!buckets) return 0;
    return ["upcoming", "open", "recently_listed", "historical", "pending_prospectus"]
      .reduce((sum, k) => sum + ((buckets[k] || []).length), 0);
  }, [buckets]);

  if (loading) return <LoadingState message="Loading IPOs…" />;
  if (error) return <ErrorState description={error} />;

  const renderCards = (rows, testIdPrefix) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => (
          <IpoCard key={r.symbol} ipo={r} testId={`${testIdPrefix}-${r.symbol}`} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="ipo-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            <h1 className="font-display text-xl font-semibold">IPO Analysis</h1>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Comprehensive CSE IPO research — prospectus-driven valuation,
            SUBSCRIBE/WATCHLIST/AVOID signal, and transparent track record.
          </p>
        </div>
        {isAdmin && (
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <Link to="/admin/market-intelligence#ipo" data-testid="ipo-admin-manage-link">
              <FileText className="h-3.5 w-3.5" /> Manage IPOs
            </Link>
          </Button>
        )}
      </div>

      {totalCount === 0 && (
        <EmptyState
          icon={Rocket}
          title="No IPOs tracked yet"
          description={isAdmin
            ? "Go to Data Sources → IPO Management to add the first IPO and upload its prospectus."
            : "Once an admin has analysed a CSE IPO prospectus, you'll see the SUBSCRIBE / AVOID signal and full valuation here."}
          data-testid="ipo-empty-state"
        />
      )}

      <Section title="Open for Subscription" Icon={Sparkles} accent="text-[hsl(var(--success))]" testId="ipo-open-section">
        {renderCards(buckets?.open, "ipo-open")}
      </Section>
      <Section title="Upcoming IPOs" Icon={Calendar} accent="text-[hsl(var(--info))]" testId="ipo-upcoming-section">
        {renderCards(buckets?.upcoming, "ipo-upcoming")}
      </Section>
      <Section title="Recently Listed" Icon={TrendingUp} accent="text-primary" testId="ipo-recent-section">
        {renderCards(buckets?.recently_listed, "ipo-recent")}
      </Section>
      <Section title="Historical IPO Analyses" Icon={Clock} testId="ipo-historical-section">
        {renderCards(buckets?.historical, "ipo-historical")}
      </Section>
      {isAdmin && (buckets?.pending_prospectus?.length || 0) > 0 && (
        <Section title="Awaiting Prospectus (Admin)" Icon={FileText} accent="text-[hsl(var(--warning))]" testId="ipo-pending-section">
          {renderCards(buckets?.pending_prospectus, "ipo-pending")}
        </Section>
      )}

      <TrackRecordCard stats={stats} />
    </div>
  );
};

export default IpoPage;
